/**
 * W16 — 工作区变量面板
 *
 * refreshVars() 从后端 /api/session/{id}/varinfo 拉取变量列表，
 * 更新 #var-tbody 表格（名称 / 大小 / 类型 / 值）。
 *
 * session_id 直接从 sessionStorage 读取（由 ws.js 写入），
 * 避免与 ws.js 产生循环依赖。
 *
 * 调用时机由 main.js 通过监听 ty:done 自定义事件触发。
 */

function getSessionId() {
  return sessionStorage.getItem('ty_session_id');
}

function formatScalar(value) {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : String(value);
  }
  if (typeof value === 'boolean' || value === null) {
    return String(value);
  }
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value);
}

function isScalarArray(value) {
  return Array.isArray(value) && value.every(item => !Array.isArray(item));
}

function formatVector(values) {
  return `[${values.map(formatScalar).join(', ')}]`;
}

function formatMatrix(rows) {
  const formattedRows = rows.map(row => `  ${formatVector(row)}`);
  return `[\n${formattedRows.join('\n')}\n]`;
}

function formatValue(value) {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
    return String(value);
  }

  if (isScalarArray(value)) {
    return formatVector(value);
  }

  if (Array.isArray(value) && value.every(isScalarArray)) {
    return formatMatrix(value);
  }

  return JSON.stringify(value, null, 2);
}

function ensureValueModal() {
  let modal = document.getElementById('var-value-modal');
  if (modal) return modal;

  modal = document.createElement('div');
  modal.id = 'var-value-modal';
  modal.className = 'modal';
  modal.innerHTML = `
    <div class="modal-content var-value-modal-content">
      <div class="modal-header">
        <span id="var-value-title">变量详情</span>
        <span class="modal-close" id="var-value-close">&times;</span>
      </div>
      <div class="modal-body var-value-modal-body">
        <div class="var-value-meta" id="var-value-meta"></div>
        <pre class="var-value-pre" id="var-value-pre"></pre>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  modal.querySelector('#var-value-close').addEventListener('click', () => {
    modal.style.display = 'none';
  });

  modal.addEventListener('click', (event) => {
    if (event.target === modal) {
      modal.style.display = 'none';
    }
  });

  return modal;
}

export async function showVariableDetail(varName) {
  const sessionId = getSessionId();
  if (!sessionId) return;

  let data;
  try {
    const res = await fetch(`/api/session/${sessionId}/variable/${encodeURIComponent(varName)}`);
    if (!res.ok) {
      console.error('[Workspace] Variable fetch failed:', res.status, res.statusText);
      return;
    }
    data = await res.json();
  } catch (err) {
    console.error('[Workspace] Variable network error:', err);
    return;
  }

  const modal = ensureValueModal();
  modal.querySelector('#var-value-title').textContent = `变量详情: ${data.name}`;
  modal.querySelector('#var-value-meta').textContent = `${data.rows}×${data.cols} ${data.type}`;
  modal.querySelector('#var-value-pre').textContent = formatValue(data.value);
  modal.style.display = 'block';
}

/** 刷新变量面板。session 未就绪或面板不存在时静默跳过。 */
export async function refreshVars() {
  const sessionId = getSessionId();
  console.log('[Workspace] Refreshing variables for session:', sessionId);
  if (!sessionId) return;

  let data;
  try {
    const res = await fetch(`/api/session/${sessionId}/varinfo`);
    if (!res.ok) {
        console.error('[Workspace] Fetch failed:', res.status, res.statusText);
        return;
    }
    data = await res.json();
    console.log('[Workspace] Received data:', data);
  } catch (err) {
    console.error('[Workspace] Network error:', err);
    return;
  }

  const builtins = ['pi', 'inf', 'Inf', 'nan', 'NaN', 'eps', 'e', 'realmax', 'realmin', 'intmax', 'intmin', 'nargin', 'nargout'];
  const tbody = document.getElementById('var-tbody');
  if (!tbody) return;

  tbody.innerHTML = '';
  (data.vars || [])
    .filter(v => !builtins.includes(v.name))
    .forEach(({ name, rows, cols, type }) => {
    const tr = document.createElement('tr');
    const td = (text) => {
      const el = document.createElement('td');
      el.textContent = text ?? '';
      return el;
    };
    const actionTd = document.createElement('td');
    const button = document.createElement('button');
    button.className = 'var-view-btn';
    button.textContent = '查看';
    button.addEventListener('click', () => {
      showVariableDetail(name);
    });
    actionTd.appendChild(button);

    tr.appendChild(td(name));
    tr.appendChild(td(`${rows}×${cols}`));
    tr.appendChild(td(type));
    tr.appendChild(actionTd);
    tbody.appendChild(tr);
  });
}
